/**
 * InvoiceList / PendingInvoiceList - the S3 and S4 worklist bodies: the table
 * on a wide viewport, one card per invoice on a phone (SIH-17 I-8a). Same rows,
 * same fields; only the presentation changes with the viewport.
 *
 * Each invoice identifier links to the invoice's S5 detail (I-8c). Because these
 * bodies exist only while rows are on screen, they also keep the reader's place:
 * leaving for an invoice remembers the scroll position, and coming back to the
 * same list puts the reader there once (no timer, no read).
 */

import { useLayoutEffect } from 'react'
import { listScrollKey, rememberListScrollOnLeave, restoreScroll } from '@lib/scrollMemory'
import type { InvoiceRow } from '@domain/types'
import { InvoiceCards, PendingInvoiceCards } from './InvoiceCards'
import { InvoiceTable } from './InvoiceTable'
import { PendingInvoiceTable } from './PendingInvoiceTable'
import { useNarrowViewport } from './useNarrowViewport'

function useListScroll(kind: 'failed' | 'pending', dbName: string): void {
  // A layout effect, on purpose: its cleanup runs while the list's DOM is still mounted, so
  // window.scrollY is still the reader's position. A passive cleanup runs after the shorter
  // invoice page has replaced the list, when the browser has already clamped the scroll to 0.
  useLayoutEffect(() => {
    const key = listScrollKey(kind, dbName)
    restoreScroll(key)
    return () => rememberListScrollOnLeave(key, window.location.pathname, window.scrollY)
  }, [kind, dbName])
}

export function InvoiceList({ rows, dbName }: { rows: readonly InvoiceRow[]; dbName: string }) {
  const narrow = useNarrowViewport()
  useListScroll('failed', dbName)
  return narrow ? <InvoiceCards rows={rows} dbName={dbName} /> : <InvoiceTable rows={rows} dbName={dbName} />
}

export function PendingInvoiceList({ rows, dbName, now }: { rows: readonly InvoiceRow[]; dbName: string; now: Date }) {
  const narrow = useNarrowViewport()
  useListScroll('pending', dbName)
  return narrow ? (
    <PendingInvoiceCards rows={rows} dbName={dbName} now={now} />
  ) : (
    <PendingInvoiceTable rows={rows} dbName={dbName} now={now} />
  )
}
